import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { 
  Plus, 
  Search, 
  Users, 
  Calendar,
  MoreVertical,
  Eye,
  Edit,
  Settings,
  Building,
  Crown,
  Shield
} from 'lucide-react'
import { apiClient } from '@/services/api'
import type { Organization, OrganizationMembership } from '@/types'

export function Organizations() {
  const [organizations, setOrganizations] = useState<Organization[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [selectedOrg, setSelectedOrg] = useState<Organization | null>(null)

  useEffect(() => {
    loadOrganizations()
  }, [])

  const loadOrganizations = async () => {
    try {
      setLoading(true)
      const response = await apiClient.getUserOrganizations({
        search: searchTerm || undefined
      })
      setOrganizations(response.data.organizations)
    } catch (err) {
      setError('Failed to load organizations')
    } finally {
      setLoading(false)
    }
  }

  const handleSearch = (term: string) => {
    setSearchTerm(term)
    // Debounce search - you could add useDebounce hook here
    setTimeout(() => loadOrganizations(), 300)
  }

  const getRoleIcon = (role: string) => {
    switch (role) {
      case 'owner': return <Crown className="w-4 h-4 text-theme-warning" />
      case 'admin': return <Shield className="w-4 h-4 text-theme-primary" />
      default: return <Users className="w-4 h-4 text-theme-muted" />
    }
  }

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'owner': return 'text-theme-warning bg-yellow-500/10'
      case 'admin': return 'text-theme-primary bg-[var(--color-primary-subtle,rgba(0,255,136,0.1))]'
      default: return 'text-theme-muted bg-surface-hover'
    }
  }

  if (loading) {
    return (
      <div className="space-y-8">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-theme-primary" />
          <span className="ml-2 text-lg text-theme-muted">Loading organizations...</span>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="space-y-8">
        <div className="bg-red-500/10 border border-theme-danger rounded-lg p-4">
          <p className="text-theme-danger">{error}</p>
          <button
            onClick={loadOrganizations}
            className="mt-2 text-theme-danger hover:opacity-80 underline"
          >
            Try again
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-col sm:flex-row sm:items-center sm:justify-between"
      >
        <div>
          <h1 className="text-3xl font-bold text-theme-text">Organizations</h1>
          <p className="text-theme-text-muted mt-1">Manage your organizations and teams</p>
        </div>
        
        <button
          onClick={() => setShowCreateModal(true)}
          className="ds-btn ds-btn-primary mt-4 sm:mt-0"
        >
          <Plus className="w-4 h-4 mr-2" />
          Create Organization
        </button>
      </motion.div>

      {/* Search */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="bg-surface p-4 rounded-lg border border-theme-border"
      >
        <div className="relative">
          <Search className="absolute left-3 top-3 h-4 w-4 text-theme-muted" />
          <input
            type="text"
            placeholder="Search organizations..."
            value={searchTerm}
            onChange={(e) => handleSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-theme-border rounded-lg bg-theme-input text-theme-text focus:ring-2 focus:ring-[var(--color-primary)] focus:border-transparent"
          />
        </div>
      </motion.div>

      {/* Organizations Grid */}
      {organizations.length === 0 ? (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="text-center py-12"
        >
          <div className="text-theme-muted mb-4">
            <Building className="w-12 h-12 mx-auto" />
          </div>
          <h3 className="text-lg font-medium text-theme-text mb-2">No organizations found</h3>
          <p className="text-theme-text-muted mb-4">
            {searchTerm 
              ? 'Try adjusting your search terms' 
              : 'Get started by creating your first organization'
            }
          </p>
          {!searchTerm && (
            <button
              onClick={() => setShowCreateModal(true)}
              className="ds-btn ds-btn-primary"
            >
              <Plus className="w-4 h-4 mr-2" />
              Create Your First Organization
            </button>
          )}
        </motion.div>
      ) : (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
        >
          {organizations.map((org, index) => (
            <motion.div
              key={org.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 * index }}
              className="bg-surface rounded-lg border border-theme-border shadow-sm hover:shadow-md transition-shadow cursor-pointer"
              onClick={() => {/* Navigate to organization details */}}
            >
              <div className="p-6">
                {/* Organization Header */}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    {org.logo_url ? (
                      <img 
                        src={org.logo_url} 
                        alt={`${org.name} logo`}
                        className="w-10 h-10 rounded-lg object-cover"
                      />
                    ) : (
                      <div className="w-10 h-10 bg-[var(--color-primary-subtle,rgba(0,255,136,0.1))] rounded-lg flex items-center justify-center">
                        <Building className="w-5 h-5 text-theme-primary" />
                      </div>
                    )}
                    <div>
                      <h3 className="text-lg font-semibold text-theme-text">
                        {org.name}
                      </h3>
                      <div className="flex items-center space-x-2">
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getRoleColor(org.user_role)}`}>
                          {getRoleIcon(org.user_role)}
                          <span className="ml-1 capitalize">{org.user_role}</span>
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Actions Menu */}
                  <div className="relative">
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        setSelectedOrg(selectedOrg?.id === org.id ? null : org)
                      }}
                      className="p-1 text-theme-muted hover:text-theme-text rounded"
                    >
                      <MoreVertical className="w-4 h-4" />
                    </button>
                    
                    {selectedOrg?.id === org.id && (
                      <div className="absolute right-0 top-8 w-48 bg-[var(--color-dropdown-bg,var(--color-surface))] rounded-lg shadow-lg border border-theme-border py-2 z-10">
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            /* Navigate to organization details */
                          }}
                          className="w-full text-left px-4 py-2 text-sm text-theme-text hover:bg-surface-hover flex items-center"
                        >
                          <Eye className="w-4 h-4 mr-2" />
                          View Details
                        </button>
                        {(org.user_role === 'owner' || org.user_role === 'admin') && (
                          <>
                            <button
                              onClick={(e) => {
                                e.stopPropagation()
                                /* Navigate to organization edit */
                              }}
                              className="w-full text-left px-4 py-2 text-sm text-theme-text hover:bg-surface-hover flex items-center"
                            >
                              <Edit className="w-4 h-4 mr-2" />
                              Edit Organization
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation()
                                /* Navigate to organization settings */
                              }}
                              className="w-full text-left px-4 py-2 text-sm text-theme-text hover:bg-surface-hover flex items-center"
                            >
                              <Settings className="w-4 h-4 mr-2" />
                              Settings
                            </button>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* Organization Description */}
                {org.description && (
                  <p className="text-sm text-theme-text-muted mb-4 line-clamp-2">
                    {org.description}
                  </p>
                )}

                {/* Organization Stats */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-theme-primary">
                      {org.member_count || 0}
                    </div>
                    <div className="text-xs text-theme-text-muted">Members</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-theme-secondary">
                      {org.lesson_count || 0}
                    </div>
                    <div className="text-xs text-theme-text-muted">Lessons</div>
                  </div>
                </div>

                {/* Recent Activity */}
                {org.last_activity && (
                  <div className="mt-4 pt-4 border-t border-theme-border">
                    <div className="flex items-center text-xs text-theme-muted">
                      <Calendar className="w-3 h-3 mr-1" />
                      <span>Last activity: {new Date(org.last_activity).toLocaleDateString()}</span>
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          ))}
        </motion.div>
      )}

      {/* Create Organization Modal */}
      {showCreateModal && (
        <CreateOrganizationModal
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => {
            setShowCreateModal(false)
            loadOrganizations()
          }}
        />
      )}
    </div>
  )
}

// Simple Create Organization Modal Component
function CreateOrganizationModal({ 
  onClose, 
  onSuccess 
}: { 
  onClose: () => void
  onSuccess: () => void
}) {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    website: ''
  })
  const [loading, setLoading] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    
    try {
      await apiClient.createOrganization(formData)
      onSuccess()
    } catch {
      // creation failed
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
      <div className="bg-[var(--color-dropdown-bg,var(--color-surface))] rounded-lg p-6 w-full max-w-md border border-theme-border">
        <h2 className="text-xl font-semibold mb-4 text-theme-text">Create New Organization</h2>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-theme-text-muted mb-1">
              Organization Name *
            </label>
            <input
              type="text"
              required
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-3 py-2 border border-theme-border rounded-lg bg-theme-input text-theme-text focus:ring-2 focus:ring-[var(--color-primary)] focus:border-transparent"
              placeholder="Enter organization name"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-theme-text-muted mb-1">
              Description
            </label>
            <textarea
              rows={3}
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="w-full px-3 py-2 border border-theme-border rounded-lg bg-theme-input text-theme-text focus:ring-2 focus:ring-[var(--color-primary)] focus:border-transparent"
              placeholder="Describe your organization"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-theme-text-muted mb-1">
              Website
            </label>
            <input
              type="url"
              value={formData.website}
              onChange={(e) => setFormData({ ...formData, website: e.target.value })}
              className="w-full px-3 py-2 border border-theme-border rounded-lg bg-theme-input text-theme-text focus:ring-2 focus:ring-[var(--color-primary)] focus:border-transparent"
              placeholder="https://your-website.com"
            />
          </div>
          
          <div className="flex space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 text-theme-text bg-surface-hover rounded-lg hover:bg-[var(--color-input-bg)] transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="ds-btn ds-btn-primary flex-1"
            >
              {loading ? 'Creating...' : 'Create Organization'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}